<template>
  <div class="captcha-wrapper">
    <div v-if="widgetId === null" class="captcha-placeholder">
      <span>Loading security check...</span>
    </div>

    <div
        ref="recaptchaEl"
        class="g-recaptcha"
        v-show="widgetId !== null"
    ></div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, nextTick, computed } from 'vue';
import { useGoogleStore } from '../stores/google';

const props = defineProps({
  size: { type: String, default: 'compact' }
});

const emit = defineEmits(['verify', 'expired']);

const googleStore = useGoogleStore();
const recaptchaEl = ref(null);
const widgetId = ref(null);
const getSiteKey = computed(() => googleStore.getSiteKey);

// 1. Global Bridge (Handles the script being loaded anywhere in the app)
const setupScript = () => {
  window.onRecaptchaLoad = () => {
    window.dispatchEvent(new Event('recaptcha-ready'));
  };

  if (!document.getElementById('recaptcha-script')) {
    const script = document.createElement('script');
    script.id = 'recaptcha-script';
    script.src = "https://www.google.com/recaptcha/api.js?render=explicit&onload=onRecaptchaLoad";
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }
};

const renderWidget = async () => {
  if (window.grecaptcha?.render && recaptchaEl.value) {
    try {
      widgetId.value = window.grecaptcha.render(recaptchaEl.value, {
        sitekey: getSiteKey.value,
        size: props.size,
        callback: (token) => emit('verify', token),
        'expired-callback': () => {
          emit('expired');
          emit('verify', '');
        }
      });
    } catch (e) {
      console.warn("reCAPTCHA render error (likely already rendered):", e);
    }
  } else {
    window.addEventListener('recaptcha-ready', renderWidget, { once: true });
  }
};

// Public method so parents can trigger a reset (e.g., after a failed form submit)
const reset = () => {
  if (window.grecaptcha && widgetId.value !== null) {
    window.grecaptcha.reset(widgetId.value);
    emit('verify', '');
  }
};

defineExpose({ reset });

onMounted(async () => {
  setupScript();
  await nextTick();
  renderWidget();
});

onUnmounted(() => {
  window.removeEventListener('recaptcha-ready', renderWidget);
});
</script>

<style scoped>
.captcha-wrapper {
  margin: 15px 0;
  min-height: 80px;
}
.captcha-placeholder {
  width: 164px;
  height: 78px;
  background-color: #eee;
  border: 1px dashed #ccc;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #666;
}
</style>